import { MktButton, MktHero, MktSection } from "../components/marketing";
import { Seo } from "../lib/seo";

/**
 * Public /about page — what ODIN is, the philosophy behind it, and a clear way to
 * get in touch. No fabricated team, company, customer or scale claims; just an
 * honest description of intent and safety posture, plus real contact routes.
 */
const HELLO_EMAIL = "hello@odinforge.io";

export function About(): JSX.Element {
  return (
    <>
      <Seo
        title="About ODIN — a calm control plane for one Discord community"
        description="ODIN is independently owned and operated by OKMedia (Norway). Our philosophy: calm, safe, beginner-friendly and honest Discord community management."
        path="/about"
      />
      <MktHero
        kicker="About"
        title="ODIN is a calm control plane for one Discord community"
        subhead="It exists to make running a community server feel considered rather than chaotic — where you understand each change and approve it before it happens."
      />

      <MktSection kicker="What it is" title="One community, managed end to end">
        <p className="mkt-section__lead">
          ODIN focuses on a single Discord server at a time: designing its roles, channels, onboarding and
          moderation, reviewing changes safely, and deploying them only when you choose to. ODIN connects
          to your server using its own managed all-in-one bot — there is no developer portal to visit and
          no bot token for you to create or hold. We'd rather do one server exceptionally well than spread
          thin across many.
        </p>
      </MktSection>

      <MktSection kicker="Who it's for" title="Owners who care about getting it right">
        <p className="mkt-section__lead">
          ODIN is for community owners and moderators who want their server to feel intentional — not
          people who enjoy living dangerously in the Discord settings panel. Whether you're setting up a
          brand-new community from a template or bringing structure to one that grew organically, ODIN
          gives you a place to plan changes, understand them, and apply them without fear. You don't need
          to be technical to use it.
        </p>
      </MktSection>

      <MktSection kicker="Philosophy" title="Calm, safe, beginner-friendly, honest">
        <div className="mkt-prose">
          <ul>
            <li>
              <strong>Calm.</strong> Plain-language explanations and generous whitespace over dashboards
              full of noise. BALDER is there to explain, not to rush you.
            </li>
            <li>
              <strong>Safe.</strong> Changes are previewed as dry runs and held for your approval. Live
              deployment is locked by default and only enabled deliberately.
            </li>
            <li>
              <strong>Beginner-friendly.</strong> Guided onboarding, templates to start from, and honest
              empty states so you always know what is about to happen.
            </li>
            <li>
              <strong>Honest.</strong> No fabricated metrics or guarantees. When something is not ready
              yet, we say so, and we make no uptime promises.
            </li>
          </ul>
        </div>
      </MktSection>

      <MktSection kicker="Safety posture" title="You approve every change">
        <p className="mkt-section__lead">
          Nothing structural reaches your Discord server without an explicit approval from you. BALDER can
          prepare and explain, but it never deploys or approves on its own, and live deployment stays
          locked until you turn it on for a specific release.
        </p>
      </MktSection>

      <div className="mkt-container">
        <div className="mkt-cta">
          <p className="mkt-cta__kicker">Get in touch</p>
          <h2>Talk to a human</h2>
          <p>
            Questions about whether ODIN fits your community, feedback, or just want to say hello? We read
            every message and reply personally — no chatbot maze, no pressure.
          </p>
          <div className="mkt-hero__actions">
            <MktButton href={`mailto:${HELLO_EMAIL}`} variant="primary">
              Email {HELLO_EMAIL}
            </MktButton>
            <MktButton to="/contact" variant="secondary">
              Visit the contact page
            </MktButton>
          </div>
        </div>
      </div>
    </>
  );
}
